import beneficiaryImg from "@/shared/assets/illustrations/glossary/beneficiary.svg";
import executorImg from "@/shared/assets/illustrations/glossary/executor.svg";
import estateImg from "@/shared/assets/illustrations/glossary/estate.svg";
import heirImg from "@/shared/assets/illustrations/glossary/heir.svg";
import lettersImg from "@/shared/assets/illustrations/glossary/letters.svg";
import poaImg from "@/shared/assets/illustrations/glossary/poa.svg";
import probateImg from "@/shared/assets/illustrations/glossary/probate.svg";
import trustImg from "@/shared/assets/illustrations/glossary/trust.svg";

export interface GlossaryTerm {
  id: string;
  title: string;
  image: string;
  summary: string;
  definition: string[];
}

export const glossaryTerms: GlossaryTerm[] = [
  {
    id: "beneficiary",
    title: "Beneficiary",
    image: beneficiaryImg,
    summary:
      "A beneficiary is someone who inherits assets from an estate, whether through a will, trust, life insurance policy, retirement account, or other financial accounts. Beneficiaries can be family members, friends, organizations, or charities.",
    definition: [
      "A beneficiary is someone who inherits assets from an estate. This could be through a will, a trust, life insurance policy, retirement account, or other financial accounts. Beneficiaries can be family members, friends, organizations, or even charities.",
      "There are primary beneficiaries (first in line to inherit) and contingent beneficiaries (who inherit if the primary beneficiary can't). It's important to keep beneficiary designations up to date, as these designations typically override what's written in a will.",
      "Understanding your role as a beneficiary helps ensure you receive what was intended for you and can make informed decisions during the estate settlement process.",
    ],
  },
  {
    id: "executor",
    title: "Executor",
    image: executorImg,
    summary:
      "An executor is the person named in a will to manage the deceased person's estate. They're responsible for gathering assets, paying debts and taxes, and distributing what remains to the beneficiaries.",
    definition: [
      "An executor is the person named in a will to manage the deceased person's estate. They're responsible for gathering assets, paying debts and taxes, and distributing what remains to the beneficiaries according to the will.",
      "If there's no will, the court appoints an administrator who performs the same duties. Being an executor is a significant responsibility that requires attention to detail, organization, and often patience as the process can take several months to over a year.",
      "Executors have a legal duty to act in the best interest of the estate and its beneficiaries. Elayne helps executors stay organized and confident throughout the entire process.",
    ],
  },
  {
    id: "estate",
    title: "Estate",
    image: estateImg,
    summary:
      "An estate is everything a person owns at the time of their death, including real property, personal property, financial accounts, investments, business interests, and digital assets.",
    definition: [
      "An estate is everything a person owns at the time of their death. This includes real property like homes and land, personal property like vehicles and jewelry, financial accounts, investments, business interests, and even digital assets.",
      "The estate also includes any debts or obligations the person had. During estate settlement, assets are used to pay off debts, taxes, and expenses before anything is distributed to beneficiaries. The total value of the estate determines whether probate is required and what taxes may apply.",
      "Properly inventorying and valuing an estate is one of the first important steps in the settlement process, and Elayne helps families organize this information.",
    ],
  },
  {
    id: "heir",
    title: "Heir",
    image: heirImg,
    summary:
      "An heir is someone legally entitled to inherit from a deceased person's estate under state intestacy laws. Unlike beneficiaries named in a will, heirs are determined by family relationship.",
    definition: [
      "An heir is someone legally entitled to inherit from a deceased person's estate under state intestacy laws. Unlike beneficiaries who are specifically named in a will, heirs are determined by their family relationship to the deceased.",
      "When someone dies without a will (intestate), state law determines which heirs inherit and in what proportion. Typically, spouses and children are first in line, followed by parents, siblings, and more distant relatives.",
      "Understanding the difference between heirs and beneficiaries is important during estate settlement, as it affects who has legal standing and what rights they have in the process.",
    ],
  },
  {
    id: "letters-testamentary",
    title: "Letters Testamentary",
    image: lettersImg,
    summary:
      "Letters testamentary are official documents issued by the probate court that give the executor legal authority to act on behalf of the estate. They prove to banks and institutions that you have the right to manage affairs.",
    definition: [
      "Letters testamentary are official documents issued by the probate court that give the executor legal authority to act on behalf of the estate. Think of them as the executor's credentials \u2014 they prove to banks, government agencies, and other institutions that you have the right to manage the deceased person's affairs.",
      "You'll need to present letters testamentary to close accounts, sell property, access financial information, and complete many other estate settlement tasks. Getting these letters is one of the first steps in the probate process.",
      "Elayne helps you understand when and how to obtain letters testamentary, and guides you through using them effectively to settle the estate.",
    ],
  },
  {
    id: "power-of-attorney",
    title: "Power of Attorney",
    image: poaImg,
    summary:
      "A power of attorney is a legal document that gives someone the authority to make decisions on another person's behalf. It covers financial matters, healthcare decisions, or both.",
    definition: [
      "A power of attorney (POA) is a legal document that gives someone the authority to make decisions on another person's behalf. There are different types: financial POA handles money and property matters, while healthcare POA covers medical decisions.",
      "A POA is only valid while the person is alive. Once someone passes away, the power of attorney ends immediately, and the executor or administrator takes over responsibility for the estate. However, POA documents can be important during estate settlement to understand what actions were taken before death.",
      "If you held power of attorney for someone who passed away, Elayne can help you understand how to transition your role and what documentation may be needed during estate settlement.",
    ],
  },
  {
    id: "probate",
    title: "Probate",
    image: probateImg,
    summary:
      "Probate is the legal process for settling someone's estate after they pass away. The court validates the will, if there is one, and ensures assets are distributed to the right people.",
    definition: [
      "Probate is the legal process for settling someone's estate after they pass away. The court validates the will (if there is one) and ensures assets are distributed to the right people. Without a will, state law determines who inherits.",
      "Not everyone has to go through probate. Some estates qualify for a simpler process if the total value is small, or if most assets were jointly owned or placed in a trust. But for many families, especially when there's a home or significant assets involved, probate is required.",
      "Each year, over 2 million Americans go through the probate process \u2014 it's common, but can be confusing and time-consuming without the right support. That's why Elayne is here to help make it clearer.",
    ],
  },
  {
    id: "trust",
    title: "Trust",
    image: trustImg,
    summary:
      "A trust is a legal arrangement where one person holds and manages assets for the benefit of others. Trusts can help avoid probate, provide privacy, and ensure assets are distributed according to wishes.",
    definition: [
      "A trust is a legal arrangement where one person (the trustee) holds and manages assets for the benefit of others (the beneficiaries). Trusts are created during someone's lifetime (living trust) or upon death through a will (testamentary trust).",
      "A revocable living trust allows someone to maintain control of their assets while alive and designate how they should be distributed after death, often avoiding probate entirely. Trusts can also provide privacy, as they don't go through public court proceedings like wills do.",
      "Understanding whether a trust exists and how it works is crucial for estate settlement. Elayne helps families identify trust assets and coordinate with trustees to ensure proper distribution.",
    ],
  },
];
